import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface InventoryRecipe {
  _id: string;
  title: string;
  titleArabic?: string;
  media?: {
    thumbnail?: string;
    images?: string[];
  };
  costEstimateEGP: number;
  buyBoxAllowed?: boolean;
  buyPreparedMealAllowed?: boolean;
  inventoryQuantityMealBox?: number;
  inventoryQuantityPreparedMeal?: number;
  lowStockThreshold?: number;
  createdAt: string;
  updatedAt: string;
}

export default function InventoryTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [recipes, setRecipes] = useState<InventoryRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, _setLimit] = useState(20);

  // Editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editInventoryMealBox, setEditInventoryMealBox] = useState<number>(0);
  const [editInventoryPrepared, setEditInventoryPrepared] = useState<number>(0);
  const [editThreshold, setEditThreshold] = useState<number>(10);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRecipes();
  }, [currentPage, limit, activeSearch, lowStockOnly]);

  const loadRecipes = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit: limit,
      };

      if (activeSearch) params.search = activeSearch;
      if (lowStockOnly) params.lowStockOnly = 'true';

      const response = await api.get('/admin/inventory', { params });
      if (response.data.success) {
        setRecipes(response.data.data || []);
        setTotalPages(response.data.pages || 1);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setLowStockOnly(false);
    setCurrentPage(1);
  };

  const startEdit = (recipe: InventoryRecipe) => {
    setEditingId(recipe._id);
    setEditInventoryMealBox(recipe.inventoryQuantityMealBox ?? 0);
    setEditInventoryPrepared(recipe.inventoryQuantityPreparedMeal ?? 0);
    setEditThreshold(recipe.lowStockThreshold || 10);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditInventoryMealBox(0);
    setEditInventoryPrepared(0);
    setEditThreshold(10);
  };

  const saveInventory = async (recipeId: string) => {
    try {
      setSaving(true);
      await api.put(`/admin/inventory/${recipeId}`, {
        inventoryQuantityMealBox: editInventoryMealBox,
        inventoryQuantityPreparedMeal: editInventoryPrepared,
        lowStockThreshold: editThreshold,
      });
      await loadRecipes();
      setEditingId(null);
    } catch (error: any) {
      console.error('Error updating inventory:', error);
      alert(error.response?.data?.message || t('inventory.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const getStockStatus = (recipe: InventoryRecipe) => {
    const box = recipe.inventoryQuantityMealBox ?? 0;
    const prepared = recipe.inventoryQuantityPreparedMeal ?? 0;
    const threshold = recipe.lowStockThreshold || 10;
    const boxAllowed = recipe.buyBoxAllowed === true;
    const preparedAllowed = recipe.buyPreparedMealAllowed === true;
    const effectiveBox = boxAllowed ? box : 0;
    const effectivePrepared = preparedAllowed ? prepared : 0;
    const totalEffective = effectiveBox + effectivePrepared;
    const anyLow = (boxAllowed && box > 0 && box <= threshold) || (preparedAllowed && prepared > 0 && prepared <= threshold);

    if (totalEffective === 0) {
      return { label: t('inventory.outOfStock'), color: 'bg-red-100 text-red-800' };
    } else if (anyLow || (boxAllowed && box <= threshold && box > 0) || (preparedAllowed && prepared <= threshold && prepared > 0)) {
      return { label: t('inventory.lowStock'), color: 'bg-yellow-100 text-yellow-800' };
    } else {
      return { label: t('inventory.inStock'), color: 'bg-green-100 text-green-800' };
    }
  };

  const fixImageUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000'}${url}`;
  };

  return (
    <div className={`space-y-6 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('inventory.title')}</h2>
        <p className="text-gray-600">{t('inventory.manageDescription')}</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4">
        <div className={`flex flex-col md:flex-row gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder={t('inventory.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent ${isRtl ? 'pr-10 pl-4' : 'pl-10 pr-4'}`}
            />
            <MagnifyingGlassIcon className={`absolute top-2.5 h-5 w-5 text-gray-400 ${isRtl ? 'right-3 left-auto' : 'left-3'}`} />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''} ${
              showFilters || lowStockOnly
                ? 'bg-primary-orange text-white border-primary-orange'
                : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            {t('inventory.filters')}
          </button>
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            {t('common.search')}
          </button>
        </div>
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className={`flex flex-wrap gap-4 items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
              <label className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <input
                  type="checkbox"
                  checked={lowStockOnly}
                  onChange={(e) => {
                    setLowStockOnly(e.target.checked);
                    setCurrentPage(1);
                  }}
                  className="rounded border-gray-300 text-primary-orange focus:ring-primary-orange"
                />
                <span className="text-sm text-gray-700">{t('inventory.lowStockOnly')}</span>
              </label>
              {(activeSearch || lowStockOnly) && (
                <button
                  onClick={clearFilters}
                  className={`flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 ${isRtl ? 'mr-auto ml-0' : 'ml-auto'}`}
                >
                  <XMarkIcon className="h-4 w-4" />
                  {t('common.clearFilters')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
            <p className="mt-2 text-gray-600">{t('inventory.loading')}</p>
          </div>
        ) : recipes.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">{t('inventory.noRecipesWithBuying')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('inventory.recipe')}</th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('inventory.price')}</th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('inventory.stockStatus')}</th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('inventory.mealBoxQty')}</th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('inventory.preparedQty')}</th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('inventory.lowStockThreshold')}</th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('inventory.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recipes.map((recipe) => {
                    const stockStatus = getStockStatus(recipe);
                    const isEditing = editingId === recipe._id;

                    return (
                      <tr key={recipe._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                            {recipe.media?.thumbnail && (
                              <img
                                src={fixImageUrl(recipe.media.thumbnail)}
                                alt={recipe.title}
                                className={`h-10 w-10 rounded object-cover ${isRtl ? 'ml-3 mr-0' : 'mr-3'}`}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40';
                                }}
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">{recipe.title}</div>
                              {recipe.titleArabic && (
                                <div className="text-sm text-gray-500">{recipe.titleArabic}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{recipe.costEstimateEGP} EGP</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${stockStatus.color}`}>
                            {stockStatus.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              value={editInventoryMealBox}
                              onChange={(e) => setEditInventoryMealBox(parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                              disabled={saving}
                            />
                          ) : (
                            <div className="text-sm text-gray-900">{recipe.inventoryQuantityMealBox ?? 0}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              value={editInventoryPrepared}
                              onChange={(e) => setEditInventoryPrepared(parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                              disabled={saving}
                            />
                          ) : (
                            <div className="text-sm text-gray-900">{recipe.inventoryQuantityPreparedMeal ?? 0}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              value={editThreshold}
                              onChange={(e) => setEditThreshold(parseInt(e.target.value) || 10)}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                              disabled={saving}
                            />
                          ) : (
                            <div className="text-sm text-gray-900">{recipe.lowStockThreshold || 10}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {isEditing ? (
                            <div className={`flex items-center gap-3 ${isRtl ? 'justify-start flex-row-reverse' : 'justify-end'}`}>
                              <button
                                onClick={() => saveInventory(recipe._id)}
                                disabled={saving}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                              >
                                {saving ? t('inventory.saving') : t('inventory.save')}
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={saving}
                                className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                              >
                                {t('inventory.cancel')}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEdit(recipe)}
                              className="text-primary-orange hover:text-orange-600"
                            >
                              {t('inventory.edit')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of {total} recipes
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
